'use client';

import { SmallLotPricingPackaging, SmallLotPricingTier } from '@ravago/shared/page-data/models/elements';
import { Badge } from '@ravago/shared/radiance/components/badge/components/Badge/Badge';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { EstimatedDelivery } from '../../../../shared/quote-pricing/estimated-delivery';

interface Props {
   tier: SmallLotPricingTier;
   selectedQuantity: number;
   packaging: SmallLotPricingPackaging;
   hasMtsLocal?: boolean;
}

export const SmallLotActiveTierSummary = ({ tier, selectedQuantity, packaging, hasMtsLocal }: Props) => {
   const translate = useTranslations();
   const formatter = useFormatter();

   const total = { amount: selectedQuantity * tier.discountedPrice.amount, currency: tier.discountedPrice.currency };

   return (
      <Box direction="column" backgroundColor="surface-2" spacing="md" borderRadius="largest" border>
         <Box direction="column" background="white" spacing="md" gap="sm" borderRadius="largest" border>
            <Box direction="row" justify="between" items="center">
               <Badge variant="primary">{translate('dynamic-product-block.small-lot.online-price')}</Badge>
               <Typography variant="small" color="primary">
                  {translate('dynamic-product-block.small-lot.online-discount', { discount: tier.discountPercentage.toString() })}
               </Typography>
            </Box>
            <Divider />
            <Box direction="row" justify="between" items="start">
               <Box direction="column" gap="3xs">
                  <Typography variant="body-highlight">
                     {formatter.quantity({ amount: selectedQuantity, unitOfMeasure: packaging.unitOfMeasure })}
                  </Typography>
                  <Typography variant="small" color="subtle">
                     {packaging.name}
                  </Typography>
               </Box>
               <Box direction="column" gap="3xs" items="end">
                  <Box direction="row" gap="xs" items="center">
                     <Typography variant="small" color="error" lineThrough>
                        {formatter.unitPrice({ price: tier.listPrice, unitOfMeasure: packaging.unitOfMeasure })}
                     </Typography>
                     <Typography variant="body-highlight">
                        {formatter.unitPrice({ price: tier.discountedPrice, unitOfMeasure: packaging.unitOfMeasure })}
                     </Typography>
                  </Box>
                  <Typography variant="small" color="subtle">
                     {translate('dynamic-product-block.total')} {formatter.price(total, { minimumFractionDigits: 2, maximumFractionDigits: 3 })}
                  </Typography>
               </Box>
            </Box>
         </Box>
         {hasMtsLocal !== undefined && <EstimatedDelivery hasMtsLocal={hasMtsLocal} />}
      </Box>
   );
};
